import { useState } from "react";
import { Loader2, Trash2, KeyRound } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { LoginForm } from "@/components/aph/LoginForm";
import { useVault } from "@/context/VaultContext";
import { InstallBanner } from "@/components/InstallBanner";
import { OrbitalMark } from "@/components/aph/OrbitalLock";
import { Field } from "@/pages/OnboardingPage";
import { WipeDialog } from "@/components/aph/WipeDialog";

export default function UnlockPage() {
  const { unlock } = useVault();
  const [pass, setPass] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [wipeOpen, setWipeOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!pass) return;
    setBusy(true);
    setError("");
    const ok = await unlock(pass).catch(() => false);
    if (!ok) {
      setError("Invalid passphrase");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-void starfield text-ash flex items-center justify-center px-6" data-testid="unlock-page">
      <form onSubmit={submit} className="w-full max-w-sm space-y-6 rise-in">
        <div className="flex flex-col items-center text-center gap-4">
          <OrbitalMark className="w-16 h-16" status={busy ? "connected" : "pending"} spinning={busy} />
          <div>
            <h1 className="font-branding text-2xl font-semibold tracking-[0.25em]">APHELION</h1>
            <p className="text-sm text-ash/45 mt-1">Vault sealed. Enter your passphrase.</p>
          </div>
        </div>
        <Field label="Passphrase or PIN" testId="unlock-pass-input" type="password" value={pass} onChange={setPass} placeholder="••••••" />
        {error && <p className="text-sm text-flare text-center" data-testid="unlock-error">{error}</p>}
        <button type="submit" disabled={busy || !pass} data-testid="unlock-submit-btn" className="w-full flex items-center justify-center gap-2 bg-stellar text-void font-branding font-semibold rounded-secure py-3 hover:opacity-85 transition-opacity disabled:opacity-50">
          {busy ? <><Loader2 size={16} className="animate-spin" /> Decrypting vault…</> : "Unlock"}
        </button>
        <button type="button" onClick={() => setLoginOpen(true)} data-testid="unlock-login-key-btn" className="w-full flex items-center justify-center gap-2 text-xs text-ash/50 hover:text-stellar transition-colors">
          <KeyRound size={14} strokeWidth={1.5} /> Log in with a login key
        </button>
        <button type="button" onClick={() => setWipeOpen(true)} data-testid="unlock-wipe-btn" className="w-full flex items-center justify-center gap-2 text-xs text-ash/35 hover:text-flare transition-colors">
          <Trash2 size={14} strokeWidth={1.5} /> Panic wipe this device
        </button>
      </form>
      <WipeDialog open={wipeOpen} onOpenChange={setWipeOpen} />
      <Dialog open={loginOpen} onOpenChange={setLoginOpen}>
        <DialogContent data-testid="unlock-login-dialog" className="bg-void border-rim text-ash sm:max-w-md rounded-secure max-h-[90vh] overflow-y-auto w-[calc(100%-2rem)]">
          <DialogHeader>
            <DialogTitle className="font-branding">Log in with a login key</DialogTitle>
            <DialogDescription className="text-flare/80">This replaces every vault on this device, including any decoy.</DialogDescription>
          </DialogHeader>
          <LoginForm onDone={() => setLoginOpen(false)} />
        </DialogContent>
      </Dialog>
      <InstallBanner />
    </div>
  );
}
